import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, InfoCircle, XCircle } from "@untitledui/icons";
import { Badge } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { SectionHeader } from "@/components/application/section-headers/section-headers";
import { AppSelect } from "@/components/app/AppSelect";
import { formatDateTime } from "@/lib/format";
import {
  NOTIFICATION_CATEGORIES,
  notificationMessageKey,
  notificationTitleKey,
  useCreateTestNotification,
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotificationSettings,
  useNotifications,
  useResetNotificationSettings,
  useUpdateNotificationSettings,
  type NotificationItem,
  type NotificationSettingsConfig,
  type NotificationSeverity,
} from "@/lib/notifications";
import { cx } from "@/utils/cx";

const PAGE_SIZE = 20;
const SEVERITIES: NotificationSeverity[] = ["INFO", "WARNING", "ERROR"];

const inputCls =
  "w-24 rounded-lg bg-primary px-3 py-2 text-sm text-primary shadow-xs ring-1 ring-primary outline-none focus:ring-2 focus:ring-brand";

/**
 * Nastavení → Oznámení: co kanál eskaluje (per kategorie), jak dlouho se drží
 * a seznam doručených oznámení.
 *
 * Nastavení jde na obecný endpoint kategorií (`/admin/api/v1/settings/notifications`),
 * takže „Reset na výchozí" i ukládání sdílí mechanismus se zbytkem nastavení.
 */
export function NotificationsPage() {
  const { t } = useTranslation();
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [page, setPage] = useState(0);

  const list = useNotifications({ unreadOnly, page, size: PAGE_SIZE });
  const markAllRead = useMarkAllNotificationsRead();

  const createTest = useCreateTestNotification();
  const [testSeverity, setTestSeverity] = useState<NotificationSeverity>("INFO");

  const settings = useNotificationSettings();
  const updateSettings = useUpdateNotificationSettings();
  const resetSettings = useResetNotificationSettings();

  // Lokální kopie, aby se dalo přepínat víc věcí a uložit je jedním tlačítkem —
  // per-přepínač PUT by při čtyřech kategoriích znamenal osm requestů.
  const [draft, setDraft] = useState<NotificationSettingsConfig | null>(null);
  useEffect(() => {
    if (settings.data?.config) {
      setDraft(structuredClone(settings.data.config));
    }
  }, [settings.data]);

  const totalPages = list.data ? Math.max(1, Math.ceil(list.data.totalItems / PAGE_SIZE)) : 1;

  const setCategory = (category: string, patch: Partial<{ enabled: boolean; minSeverity: NotificationSeverity }>) => {
    setDraft((current) => {
      if (!current) return current;
      const existing = current.categories[category] ?? { enabled: true, minSeverity: "INFO" as NotificationSeverity };
      return { ...current, categories: { ...current.categories, [category]: { ...existing, ...patch } } };
    });
  };

  return (
    <div className="flex flex-col gap-6">
      <SectionHeader.Root>
        <SectionHeader.Group>
          <div className="flex-1">
            <SectionHeader.Heading>{t("notificationSettings.title")}</SectionHeader.Heading>
            <SectionHeader.Subheading>{t("notificationSettings.subtitle")}</SectionHeader.Subheading>
          </div>
        </SectionHeader.Group>
      </SectionHeader.Root>

      {/* ------------------------------- Nastavení ------------------------------- */}
      {settings.isLoading && <LoadingIndicator />}
      {settings.isError && <p className="text-sm text-error-primary">{t("common.loadError")}</p>}

      {draft && (
        <div className="flex flex-col gap-4 rounded-xl bg-primary p-5 ring-1 ring-secondary">
          <p className="text-sm font-semibold text-primary">{t("notificationSettings.categories")}</p>
          <p className="text-sm text-tertiary">{t("notificationSettings.categoriesHint")}</p>

          <div className="flex flex-col gap-3">
            {NOTIFICATION_CATEGORIES.map((category) => {
              const value = draft.categories[category] ?? { enabled: true, minSeverity: "INFO" as NotificationSeverity };
              return (
                <div key={category} className="flex flex-wrap items-center gap-3 border-b border-secondary pb-3 last:border-0">
                  <label className="flex min-w-64 flex-1 items-center gap-2">
                    <input
                      type="checkbox"
                      checked={value.enabled}
                      onChange={(e) => setCategory(category, { enabled: e.target.checked })}
                      className="size-4 rounded border-secondary"
                    />
                    <span className="flex flex-col">
                      <span className="text-sm font-medium text-primary">{t(`notifications.category.${category}`)}</span>
                      <span className="text-xs text-tertiary">{t(`notifications.category.${category}.desc`)}</span>
                    </span>
                  </label>
                  <label className="flex items-center gap-2">
                    <span className="text-xs text-tertiary">{t("notificationSettings.minSeverity")}</span>
                    <AppSelect
                      fullWidth={false}
                      value={value.minSeverity}
                      disabled={!value.enabled}
                      onChange={(e) => setCategory(category, { minSeverity: e.target.value as NotificationSeverity })}
                    >
                      {SEVERITIES.map((severity) => (
                        <option key={severity} value={severity}>
                          {t(`notifications.severity.${severity}`)}
                        </option>
                      ))}
                    </AppSelect>
                  </label>
                </div>
              );
            })}
          </div>

          <div className="flex flex-wrap items-end gap-6">
            <label className="flex flex-col gap-1">
              <span className="text-sm font-medium text-secondary">{t("notificationSettings.retentionDays")}</span>
              <input
                type="number"
                min={1}
                max={365}
                className={inputCls}
                value={draft.retentionDays}
                onChange={(e) => setDraft({ ...draft, retentionDays: Number(e.target.value) })}
              />
              <span className="text-xs text-tertiary">{t("notificationSettings.retentionHint")}</span>
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-sm font-medium text-secondary">{t("notificationSettings.dedupWindow")}</span>
              <input
                type="number"
                min={1}
                max={1440}
                className={inputCls}
                value={draft.dedupWindowMinutes}
                onChange={(e) => setDraft({ ...draft, dedupWindowMinutes: Number(e.target.value) })}
              />
              <span className="text-xs text-tertiary">{t("notificationSettings.dedupHint")}</span>
            </label>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              isLoading={updateSettings.isPending}
              onPress={() => updateSettings.mutate(draft)}
            >
              {t("common.save")}
            </Button>
            <Button
              size="sm"
              color="secondary"
              isLoading={resetSettings.isPending}
              onPress={() => resetSettings.mutate()}
            >
              {t("notificationSettings.reset")}
            </Button>
            {updateSettings.isSuccess && <span className="self-center text-sm text-success-primary">{t("common.saved")}</span>}
          </div>
        </div>
      )}

      {/* ---------------------------- Ověření kanálu ---------------------------- */}
      <div className="flex flex-col gap-3 rounded-xl bg-primary p-5 ring-1 ring-secondary">
        <div>
          <p className="text-sm font-semibold text-primary">{t("notificationSettings.testTitle")}</p>
          <p className="text-sm text-tertiary">{t("notificationSettings.testHint")}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <AppSelect
            fullWidth={false}
            aria-label={t("notificationSettings.minSeverity")}
            value={testSeverity}
            onChange={(e) => setTestSeverity(e.target.value as NotificationSeverity)}
          >
            {SEVERITIES.map((severity) => (
              <option key={severity} value={severity}>
                {t(`notifications.severity.${severity}`)}
              </option>
            ))}
          </AppSelect>
          <Button
            size="sm"
            color="secondary"
            isLoading={createTest.isPending}
            onPress={() => createTest.mutate(testSeverity)}
          >
            {t("notificationSettings.createTest")}
          </Button>
          {createTest.isSuccess && (
            <span className="self-center text-sm text-success-primary">{t("notificationSettings.testCreated")}</span>
          )}
        </div>
      </div>

      {/* -------------------------------- Seznam -------------------------------- */}
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <p className="flex-1 text-sm font-semibold text-primary">{t("notifications.title")}</p>
          <label className="flex items-center gap-2 text-sm text-secondary">
            <input
              type="checkbox"
              checked={unreadOnly}
              onChange={(e) => {
                setUnreadOnly(e.target.checked);
                setPage(0);
              }}
              className="size-4 rounded border-secondary"
            />
            {t("notifications.unreadOnly")}
          </label>
          <Button
            size="sm"
            color="secondary"
            isDisabled={(list.data?.unreadCount ?? 0) === 0}
            isLoading={markAllRead.isPending}
            onPress={() => markAllRead.mutate()}
          >
            {t("notifications.markAllRead")}
          </Button>
        </div>

        {list.isLoading && <LoadingIndicator />}
        {list.isError && <p className="text-sm text-error-primary">{t("common.loadError")}</p>}
        {list.data && list.data.items.length === 0 && <p className="text-sm text-tertiary">{t("notifications.empty")}</p>}

        {list.data?.items.map((item) => <NotificationCard key={item.id} item={item} />)}

        {list.data && list.data.totalItems > PAGE_SIZE && (
          <div className="flex items-center gap-3">
            <Button size="sm" color="secondary" isDisabled={page === 0} onPress={() => setPage(page - 1)}>
              {t("pager.prev")}
            </Button>
            <span className="text-sm text-tertiary">
              {t("pager.pageInfo", { page: page + 1, pages: totalPages, total: list.data.totalItems })}
            </span>
            <Button size="sm" color="secondary" isDisabled={page + 1 >= totalPages} onPress={() => setPage(page + 1)}>
              {t("pager.next")}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

const SEVERITY_ICON: Record<NotificationSeverity, typeof InfoCircle> = {
  INFO: InfoCircle,
  WARNING: AlertTriangle,
  ERROR: XCircle,
};

const SEVERITY_COLOR: Record<NotificationSeverity, "gray" | "warning" | "error"> = {
  INFO: "gray",
  WARNING: "warning",
  ERROR: "error",
};

function NotificationCard({ item }: { item: NotificationItem }) {
  const { t } = useTranslation();
  const markRead = useMarkNotificationRead();
  const Icon = SEVERITY_ICON[item.severity] ?? InfoCircle;

  const title = t(notificationTitleKey(item.type), { ...item.params, defaultValue: item.title });
  const message = t(notificationMessageKey(item.type), { ...item.params, defaultValue: item.message ?? "" });

  return (
    <div
      className={cx(
        "flex items-start gap-3 rounded-xl bg-primary p-4 ring-1 ring-secondary",
        item.readAt == null && "ring-brand",
      )}
    >
      <Icon
        className={cx(
          "mt-0.5 size-5 shrink-0",
          item.severity === "ERROR"
            ? "text-fg-error-primary"
            : item.severity === "WARNING"
              ? "text-fg-warning-primary"
              : "text-fg-quaternary",
        )}
        aria-hidden="true"
      />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-primary">{title}</span>
          <Badge type="pill-color" size="sm" color={SEVERITY_COLOR[item.severity]}>
            {t(`notifications.severity.${item.severity}`)}
          </Badge>
          <Badge type="pill-color" size="sm" color="gray">
            {t(`notifications.category.${item.category}`)}
          </Badge>
          {item.occurrences > 1 && <span className="text-xs text-tertiary">×{item.occurrences}</span>}
        </div>
        {message && <p className="mt-1 text-sm text-secondary">{message}</p>}
        <p className="mt-1 text-xs text-quaternary">
          {formatDateTime(item.createdAt, { seconds: true })}
          {item.occurrences > 1 && ` · ${t("notifications.lastSeen")}: ${formatDateTime(item.lastSeenAt, { seconds: true })}`}
        </p>
      </div>
      {item.readAt == null && (
        <Button size="sm" color="link-gray" isLoading={markRead.isPending} onPress={() => markRead.mutate(item.id)}>
          {t("notifications.markRead")}
        </Button>
      )}
    </div>
  );
}
